/**
 * Modular ship construction: a ship is a list of part instances (category +
 * variant + transform) that players arrange freely in 3D. The catalog and
 * validation live here so the server can sanity-check layouts it relays and
 * every client renders identical ships.
 *
 * Geometry is client-side (built from primitives, keyed by variant id);
 * this module only knows ids and limits.
 */

/** "hull" (v49): a whole authored GLB ship in one part. A layout containing a
 *  hull part renders the GLB instead of assembling primitives — the id rides the
 *  existing PartInstance/validateLayout/refit pipeline untouched, which is the
 *  entire reason it is a category and not a new layout schema. */
export const PART_CATEGORIES = ["cockpit", "body", "wing", "thruster", "weapon", "hull"] as const;
export type PartCategory = (typeof PART_CATEGORIES)[number];

export const PART_VARIANTS: Record<PartCategory, readonly string[]> = {
  cockpit: ["bubble", "visor", "pod"],
  body: ["octa", "hull", "wedge"],
  wing: ["delta", "fin", "blade"],
  thruster: ["jet", "heavy", "ring"],
  weapon: ["cannon", "twin", "launcher"],
  hull: ["lightspeed"],
};

/** Hull variants are EARNED (collection-ledger tokens, `ship:<variant>`), unlike
 *  every other part, which is free to all. The server clamps layouts against the
 *  owner's ledger with clampLayoutToUnlocks. */
export function shipUnlockToken(hullVariant: string): string {
  return `ship:${hullVariant}`;
}

/** The one-part layout an earned hull boards as. */
export function hullLayout(variant: string): ShipLayout {
  return [{ cat: "hull", variant, p: [0, 0, 0], r: [0, 0, 0] }];
}

/** The hull variant a layout carries, or null for a part-built ship. */
export function layoutHull(layout: ShipLayout): string | null {
  for (const part of layout) if (part.cat === "hull") return part.variant;
  return null;
}

/**
 * Strip hull parts the owner has not earned (v49). CLAMPS, never rejects — the
 * rest of the layout survives, and a layout left empty falls back to
 * DEFAULT_LAYOUT so a revoked hull never strands a player shipless. The same
 * enforcement shape as clampStyleToUnlocks in rpg.ts, for the same reason: a
 * hull is broadcast to the room, so without this a hand-edited client flies
 * whatever it likes.
 */
export function clampLayoutToUnlocks(
  layout: ShipLayout,
  owned: ReadonlySet<string>,
): ShipLayout {
  const out = layout.filter((p) => p.cat !== "hull" || owned.has(shipUnlockToken(p.variant)));
  if (out.length === 0) return DEFAULT_LAYOUT.map((p) => ({ ...p }));
  return out.length === layout.length ? layout : out;
}

export interface PartInstance {
  cat: PartCategory;
  variant: string;
  /** Position in ship-local space (forward = -Z), each axis in [-B, B]. */
  p: [number, number, number];
  /** Euler rotation (XYZ order), radians. */
  r: [number, number, number];
  /** Per-axis scale; omitted means [1, 1, 1]. Each axis in [MIN,MAX]_SCALE. */
  s?: [number, number, number];
  /** Custom color as 0xRRGGBB; omitted means the ship's default palette. */
  c?: number;
  /** Weapon group (weapons only): 1 = mouse button 1, 2 = mouse button 2. */
  g?: 1 | 2;
}

export type ShipLayout = PartInstance[];

export const MAX_PARTS = 24;
/** Position bound per axis (ship-local units). */
export const PART_POS_BOUND = 8;
export const MIN_SCALE = 0.2;
export const MAX_SCALE = 5;

export const DEFAULT_LAYOUT: ShipLayout = [
  { cat: "body", variant: "octa", p: [0, 0, 0], r: [0, 0, 0] },
  { cat: "cockpit", variant: "bubble", p: [0, 0.45, -0.6], r: [0, 0, 0] },
  { cat: "wing", variant: "delta", p: [0, -0.1, 0.8], r: [0, 0, 0] },
  { cat: "wing", variant: "fin", p: [0, 0.6, 1.2], r: [0, 0, 0] },
  { cat: "thruster", variant: "jet", p: [-1.4, -0.1, 1.4], r: [0, 0, 0] },
  { cat: "thruster", variant: "jet", p: [1.4, -0.1, 1.4], r: [0, 0, 0] },
  { cat: "weapon", variant: "cannon", p: [-2.6, -0.25, 0.6], r: [0, 0, 0], g: 1 },
  { cat: "weapon", variant: "cannon", p: [2.6, -0.25, 0.6], r: [0, 0, 0], g: 1 },
];

function isFiniteTuple(v: unknown, len: number, bound: number): v is number[] {
  return (
    Array.isArray(v) &&
    v.length === len &&
    v.every((n) => typeof n === "number" && Number.isFinite(n) && Math.abs(n) <= bound)
  );
}

/**
 * Validate an untrusted layout. Returns a sanitized copy, or null if the
 * input is structurally hopeless (callers typically fall back to
 * DEFAULT_LAYOUT).
 */
export function validateLayout(raw: unknown): ShipLayout | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_PARTS) return null;
  const out: ShipLayout = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) return null;
    const o = item as Record<string, unknown>;
    const cat = o.cat as PartCategory;
    if (!PART_CATEGORIES.includes(cat)) return null;
    if (typeof o.variant !== "string" || !PART_VARIANTS[cat].includes(o.variant)) return null;
    if (!isFiniteTuple(o.p, 3, PART_POS_BOUND)) return null;
    if (!isFiniteTuple(o.r, 3, Math.PI * 2)) return null;
    if (o.g !== undefined && o.g !== 1 && o.g !== 2) return null;
    if (o.s !== undefined) {
      if (!isFiniteTuple(o.s, 3, MAX_SCALE)) return null;
      if ((o.s as number[]).some((n) => n < MIN_SCALE)) return null;
    }
    if (o.c !== undefined) {
      if (typeof o.c !== "number" || !Number.isInteger(o.c) || o.c < 0 || o.c > 0xffffff) {
        return null;
      }
    }
    const part: PartInstance = {
      cat,
      variant: o.variant,
      p: [o.p[0], o.p[1], o.p[2]],
      r: [o.r[0], o.r[1], o.r[2]],
    };
    if (o.s !== undefined) {
      const s = o.s as [number, number, number];
      part.s = [s[0], s[1], s[2]];
    }
    if (o.c !== undefined) part.c = o.c;
    if (cat === "weapon") part.g = o.g === 2 ? 2 : 1;
    out.push(part);
  }
  return out;
}
